import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { TableOfContents } from '@/components/content/TableOfContents';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';

const ARTICLE_DESCRIPTION =
  "A guide for family, friends, club members and sponsors backing an athlete: how Sport Fund works, what your contribution does, and how to stay connected.";

const FAQS = [
  {
    q: 'Does the athlete keep 100% of my contribution?',
    a: 'Yes. Athletes pay nothing to use Sport Fund and receive 100% of the amount you choose to contribute. A small, transparent fee covering payment processing and the platform is added on top of your contribution.',
  },
  {
    q: 'How does my money reach the athlete?',
    a: "Sport Fund never holds your money. Contributions are processed by Stripe and deposited directly into the athlete's bank account, typically within 1–3 business days.",
  },
  {
    q: 'Can I give a one-off amount instead of a monthly contribution?',
    a: 'Yes. On the fund page you can choose a one-off contribution of any amount, any time, or set up a monthly recurring contribution that you can cancel any time.',
  },
  {
    q: 'What payment methods can I use?',
    a: 'You can pay with a credit/debit card, a US bank transfer (ACH), or a European bank transfer (SEPA). Bank transfers carry lower fees than cards, so more of your contribution reaches the athlete.',
  },
  {
    q: 'Are contributions on Sport Fund tax-deductible?',
    a: 'No — contributions on Sport Fund are treated as personal gifts, not tax-deductible charitable donations. If you need a refund for an unauthorised contribution, contact support@thesportfund.com within 7 days.',
  },
];

const SupportingLocalChurch: React.FC = () => {
  usePageMeta('Supporting an Athlete', ARTICLE_DESCRIPTION, {
    article: true,
    image: '/resources/coach-and-athlete.jpg',
    breadcrumbs: [{ name: 'Resources', path: '/resources' }, { name: 'Supporting an Athlete' }],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  });
  const TOC_SECTIONS = [
    {
      id: "what-the-money-actually-pays-for",
      text: "What the money actually pays for"
    },
    {
      id: "how-contributing-works",
      text: "How contributing works"
    },
    {
      id: "where-your-money-goes",
      text: "Where your money goes"
    },
    {
      id: "staying-connected-to-the-journey",
      text: "Staying connected to the journey"
    },
    {
      id: "if-you-don-t-know-the-athlete-personally",
      text: "If you don't know the athlete personally"
    },
    {
      id: "a-note-on-tax-and-refunds",
      text: "A note on tax and refunds"
    },
    {
      id: "ways-to-help-beyond-money",
      text: "Ways to help beyond money"
    },
    {
      id: "frequently-asked-questions",
      text: "Frequently asked questions"
    }
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link to="/resources" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Resources
      </Link>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">Supporting an Athlete: How Your Contribution Makes a Difference</h1>
      <p className="text-gray-500 mb-8 text-lg">For family, friends, club members and sponsors — what backing an athlete on Sport Fund looks like and why it matters.</p>

      <article className="prose prose-lg max-w-none">
        <TableOfContents sections={TOC_SECTIONS} />
        <p>
          Someone you know — a son or daughter, a niece or nephew, a teammate, a junior at your club, a colleague — is asking you to back them through Sport Fund. This page explains how it works, what your contribution does, and how to make backing an athlete feel less like a charity ask and more like investing in someone you believe in.
        </p>

        <h2 id="what-the-money-actually-pays-for">What the money actually pays for</h2>
        <p>Competing at any level costs real money, and most of it is invisible to people outside the sport. Depending on the athlete's discipline and level, contributions typically go toward:</p>
        <ul>
          <li><strong>Equipment</strong> — running shoes (replaced every ~600 km), bikes, kit, racquets, balls, bats, helmets, protective gear, season-specific tools.</li>
          <li><strong>Coaching</strong> — weekly sessions with a qualified coach, often the single biggest line item.</li>
          <li><strong>Federation fees + licences</strong> — annual fees to be eligible to compete. In some sports these run into hundreds of dollars.</li>
          <li><strong>Training camps</strong> — intensive blocks at altitude, on snow, on water, on track, often abroad.</li>
          <li><strong>Competition travel</strong> — entries, flights, hotels, transfers, meals. Often the largest variable cost.</li>
          <li><strong>Recovery + healthcare</strong> — physio, massage, sports medicine, supplements.</li>
        </ul>
        <p>Most amateur and semi-pro athletes carry this themselves alongside a job, study, or family. A contribution that covers a single entry fee can be the difference between racing and watching from the side.</p>

        <h2 id="how-contributing-works">How contributing works</h2>
        <p>On the athlete's fund page, you'll see a contribute button. You can choose:</p>
        <ul>
          <li><strong>A one-off contribution</strong> — any amount, any time. Useful for marking a moment or sending a specific gift.</li>
          <li><strong>Monthly recurring</strong> — a set amount each month. This is what most athletes find most useful because it lets them plan their season. You can cancel any time.</li>
        </ul>
        <p>You pay with a credit/debit card, a US bank transfer (ACH), or a European bank transfer (SEPA). Bank transfers have lower fees than cards, so more of your contribution reaches the athlete — bear that in mind on larger or recurring contributions.</p>

        <h2 id="where-your-money-goes">Where your money goes</h2>
        <p>
          Sport Fund never holds your money. Contributions are processed by Stripe — the same payment infrastructure used by major retailers and platforms worldwide — and deposited directly into the athlete's bank account, typically within 1–3 business days.
        </p>
        <p>
          A small, transparent fee is added to your contribution to cover payment processing (Stripe's standard rates) and the platform itself. Athletes pay nothing to use Sport Fund and receive 100% of the amount you choose to contribute.
        </p>

        <h2 id="staying-connected-to-the-journey">Staying connected to the journey</h2>
        <p>Sport Fund isn't a one-off donation site. When you back an athlete, you become part of their season:</p>
        <ul>
          <li>Get email updates when they post — training, competitions, milestones.</li>
          <li>Comment on posts and send them direct encouragement.</li>
          <li>See exactly what your contribution helped enable.</li>
          <li>Cancel or adjust monthly contributions any time without awkwardness — it's all in your dashboard.</li>
        </ul>
        <p>For most supporters, this is the part that makes Sport Fund different from a one-off transaction. You're not handing over money and walking away. You're staying in the loop with someone you care about, on a journey that matters.</p>

        <h2 id="if-you-don-t-know-the-athlete-personally">If you don't know the athlete personally</h2>
        <p>
          Some supporters back athletes they don't know — people who saw a story, a video, a result, a junior from their old club. Sport Fund makes this easy: every fund page shows the athlete's sport, level, story and goal, and contributions flow with the same transparency. If you want to back a young athlete from your community, an athlete who reminds you of yourself at that age, or someone whose discipline you've followed — you can.
        </p>

        <h2 id="a-note-on-tax-and-refunds">A note on tax and refunds</h2>
        <p>
          Contributions on Sport Fund are treated as personal gifts, not tax-deductible charitable donations. If you need a refund — say, in case of an unauthorised contribution — contact <a href="mailto:support@thesportfund.com" className="text-blue-600">support@thesportfund.com</a> within 7 days and we'll work with you and Stripe to resolve it.
        </p>

        <h2 id="ways-to-help-beyond-money">Ways to help beyond money</h2>
        <ul>
          <li>Share the athlete's page with people who'd care about their sport.</li>
          <li>Comment on their updates — it costs nothing and means more than you think.</li>
          <li>If you have a connection to a sponsor, coach, club or facility that could help — make the introduction.</li>
          <li>Recurring beats one-off. Even $5/month over a season is more useful than a single $30.</li>
        </ul>
        <p>Sport has always been a community thing. The best athletes you'll ever meet were carried part of the way by family, friends, coaches and people who showed up. Sport Fund just makes that easier to do.</p>

        <h2 id="frequently-asked-questions">Frequently asked questions</h2>
        <div className="not-prose space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">{f.q}</h3>
              <p className="text-gray-600">{f.a}</p>
            </div>
          ))}
        </div>
        <ArticleFeedback articleSlug="supporting-an-athlete" />
      </article>
    </div>
  );
};

export default SupportingLocalChurch;
